import React from "react";
import PropTypes from "prop-types";
import { userActions } from "../_actions";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30; // seconds

class OtpVerification extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      otpValues: Array(OTP_LENGTH).fill(""),
      otpSubmitted: false,
      otpLoader: false,
      resendTimer: 0,
      resendInterval: null,
    };

    this.inputRefs = Array(OTP_LENGTH).fill(null).map(() => React.createRef());

    this.handleOtpChange  = this.handleOtpChange.bind(this);
    this.handleOtpKeyDown = this.handleOtpKeyDown.bind(this);
    this.handleOtpPaste   = this.handleOtpPaste.bind(this);
    this.handleOtpSubmit  = this.handleOtpSubmit.bind(this);
    this.handleResend     = this.handleResend.bind(this);
  }

  componentDidMount() {
    this.startResendTimer();
  }

  componentWillUnmount() {
    if (this.state.resendInterval) {
      clearInterval(this.state.resendInterval);
    }
  }

  startResendTimer() {
    if (this.state.resendInterval) clearInterval(this.state.resendInterval);
    this.setState({ resendTimer: RESEND_COOLDOWN });
    const interval = setInterval(() => {
      this.setState((prev) => {
        if (prev.resendTimer <= 1) {
          clearInterval(interval);
          return { resendTimer: 0, resendInterval: null };
        }
        return { resendTimer: prev.resendTimer - 1 };
      });
    }, 1000);
    this.setState({ resendInterval: interval });
  }

  handleOtpChange(index, e) {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...this.state.otpValues];
    next[index] = val;
    this.setState({ otpValues: next }, () => {
      if (val && index < OTP_LENGTH - 1) {
        this.inputRefs[index + 1].current?.focus();
      }
    });
  }

  handleOtpKeyDown(index, e) {
    if (e.key === "Backspace" && !this.state.otpValues[index] && index > 0) {
      const next = [...this.state.otpValues];
      next[index - 1] = "";
      this.setState({ otpValues: next }, () => {
        this.inputRefs[index - 1].current?.focus();
      });
    }
  }

  handleOtpPaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    this.setState({ otpValues: next }, () => {
      this.inputRefs[Math.min(pasted.length, OTP_LENGTH - 1)].current?.focus();
    });
  }

  handleOtpSubmit(e) {
    e.preventDefault();
    this.setState({ otpSubmitted: true });
    const code = this.state.otpValues.join("");
    if (code.length < OTP_LENGTH) return;

    this.setState({ otpLoader: true });
    this.props.dispatch(userActions.verifyLoginOtp(this.props.email, code)).then((res) => {
      if (!res.success) {
        this.props.onError(res.message || "Invalid verification code.");
        this.setState({
          otpValues: Array(OTP_LENGTH).fill(""),
          otpSubmitted: false,
        });
        this.inputRefs[0].current?.focus();
      }
      this.setState({ otpLoader: false });
    }).catch(() => {
      this.setState({ otpLoader: false });
    });
  }

  handleResend() {
    if (this.state.resendTimer > 0) return;
    this.setState({ otpValues: Array(OTP_LENGTH).fill(""), otpSubmitted: false });
    this.props.onResend(() => this.startResendTimer());
  }

  render() {
    const { email } = this.props;
    const { otpValues, otpSubmitted, otpLoader, resendTimer } = this.state;
    const code = otpValues.join("");
    const isComplete = code.length === OTP_LENGTH;

    return (
      <form name="otp-form" onSubmit={this.handleOtpSubmit} className="loginForm otp-form">
        <div className="otp-intro">
          <p className="otp-sub-label">Two-Step Verification</p>
          <p className="otp-desc">
            Enter the 6-digit code we sent to {email}.
          </p>
        </div>

        <div className="otp-inputs-wrapper" onPaste={this.handleOtpPaste}>
          {otpValues.map((val, i) => (
            <input
              key={i}
              ref={this.inputRefs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className={
                "otp-input" +
                (otpSubmitted && !isComplete && !val ? " otp-input--error" : "")
              }
              value={val}
              onChange={(e) => this.handleOtpChange(i, e)}
              onKeyDown={(e) => this.handleOtpKeyDown(i, e)}
              autoFocus={i === 0}
            />
          ))}
        </div>
        {otpSubmitted && !isComplete && (
          <div className="otp-error-msg">Please enter all 6 digits.</div>
        )}

        <div className="form-group btnGroup loginBtn">
          <button className="btn btn-primary" disabled={otpLoader}>
            {otpLoader ? "Verifying…" : "Verify"}
          </button>
          {otpLoader && (
            <img
              className="login_loader"
              alt="loader"
              src="data:image/gif;base64,R0lGODlhEAAQAPIAAP///wAAAMLCwkJCQgAAAGJiYoKCgpKSkiH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJCgAAACwAAAAAEAAQAAADMwi63P4wyklrE2MIOggZnAdOmGYJRbExwroUmcG2LmDEwnHQLVsYOd2mBzkYDAdKa+dIAAAh+QQJCgAAACwAAAAAEAAQAAADNAi63P5OjCEgG4QMu7DmikRxQlFUYDEZIGBMRVsaqHwctXXf7WEYB4Ag1xjihkMZsiUkKhIAIfkECQoAAAAsAAAAABAAEAAAAzYIujIjK8pByJDMlFYvBoVjHA70GU7xSUJhmKtwHPAKzLO9HMaoKwJZ7Rf8AYPDDzKpZBqfvwQAIfkECQoAAAAsAAAAABAAEAAAAzMIumIlK8oyhpHsnFZfhYumCYUhDAQxRIdhHBGqRoKw0R8DYlJd8z0fMDgsGo/IpHI5TAAAIfkECQoAAAAsAAAAABAAEAAAAzIIunInK0rnZBTwGPNMgQwmdsNgXGJUlIWEuR5oWUIpz8pAEAMe6TwfwyYsGo/IpFKSAAAh+QQJCgAAACwAAAAAEAAQAAADMwi6IMKQORfjdOe82p4wGccc4CEuQradylesojEMBgsUc2G7sDX3lQGBMLAJibufbSlKAAAh+QQJCgAAACwAAAAAEAAQAAADMgi63P7wCRHZnFVdmgHu2nFwlWCI3WGc3TSWhUFGxTAUkGCbtgENBMJAEJsxgMLWzpEAACH5BAkKAAAALAAAAAAQABAAAAMyCLrc/jDKSatlQtScKdceCAjDII7HcQ4EMTCpyrCuUBjCYRgHVtqlAiB1YhiCnlsRkAAAOwAAAAAAAAAAAA=="
            />
          )}
        </div>

        <div className="otp-resend-row">
          {resendTimer > 0 ? (
            <span className="otp-resend-disabled">
              Didn&apos;t receive the code? Resend in {resendTimer}s
            </span>
          ) : (
            <button
              type="button"
              className="btn-link-inline"
              onClick={this.handleResend}
            >
              Didn&apos;t receive the code? <span>Resend</span>
            </button>
          )}
        </div>
      </form>
    );
  }
}

OtpVerification.propTypes = {
  dispatch: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  onError: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
};

export default OtpVerification;
